/**
 * Team Inbox — feature flag (Integration Plan §1 "Feature-flagged").
 *
 * The flag lives at Firestore `settings/flags` field `teaminbox`:
 *
 *   { teaminbox: { enabled: false } }            → OFF for everyone
 *   { teaminbox: { enabled: true } }             → ON for every team member
 *   { teaminbox: { enabled: ["<uid>", …] } }     → ON for the listed uids
 *                                                   (the dark-launch shape:
 *                                                   enabled: ["rohit"]).
 *
 * DEFAULT IS OFF: a missing doc, missing field, malformed value or read
 * error all resolve to the EXISTING Messages screen. Rollback is a flag
 * flip, not a redeploy. `settings/` team-read is already rules-allowed.
 *
 * The /messages route swap itself is a MERGE-TIME diff (MERGE_NOTES.md) —
 * this module only provides the decision + the <TeamInboxGate/> wrapper
 * (exported from ../index) so the App.tsx change stays one line.
 *
 * Pure resolver below is SDK-free for the Node test runner.
 */

/** PURE — resolve the raw `teaminbox` flag value for a user. Default OFF. */
export function resolveTeamInboxFlag(
  raw: unknown,
  uid: string | null | undefined,
): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const enabled = (raw as { enabled?: unknown }).enabled;
  if (enabled === true) return true;
  if (Array.isArray(enabled)) {
    const u = String(uid || '').trim();
    if (!u) return false;
    return enabled.some((e) => typeof e === 'string' && e === u);
  }
  return false;
}

/**
 * Subscribe to the flag for ONE user. Emits `false` immediately (default
 * OFF — the existing screen renders with zero flash of the new UI), then
 * live-updates from `settings/flags`. Errors keep OFF.
 */
export function subscribeTeamInboxFlag(
  uid: string | null | undefined,
  cb: (on: boolean) => void,
): () => void {
  cb(false);
  let unsub: (() => void) | null = null;
  let cancelled = false;
  void (async () => {
    try {
      const [{ db }, { doc, onSnapshot }] = await Promise.all([
        import('../../../lib/firebase'),
        import('firebase/firestore'),
      ]);
      if (cancelled) return;
      unsub = onSnapshot(
        doc(db, 'settings', 'flags'),
        (snap) => {
          const data = snap.exists() ? snap.data() : null;
          cb(
            resolveTeamInboxFlag(
              data ? (data as { teaminbox?: unknown }).teaminbox : null,
              uid,
            ),
          );
        },
        () => cb(false),
      );
    } catch {
      /* SDK unavailable → stays OFF */
    }
  })();
  return () => {
    cancelled = true;
    if (unsub) unsub();
  };
}
